import { reflect } from '@effector/reflect';
import { NumberInput, Select } from '@mantine/core';
import { combine } from 'effector';
import { useUnit } from 'effector-react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { viewerModel } from 'entities/viewer';
import { USER_ROLES } from 'shared/api';
import { $t, translated } from 'shared/lib';
import { createModalForm } from 'shared/ui';
import { AddBonusModel } from '../../model';
import cls from './add-bonus-modal.module.scss';

export const createAddBonusModal = (model: AddBonusModel) => {
	const ModalForm = createModalForm(model);

	const AccountField = reflect({
		view: Select,
		bind: {
			'data-test-id': 'add-bonus-account-select',
			value: model.form.fields.accountId.$value.map((value) => value?.toString() || null),
			onChange: (value: string | null) =>
				model.form.fields.accountId.change(value === null ? value : Number(value)),
			data: model.$accounts,
			disabled: model.form.fields.accountId.$disabled,
			label: translated('add-bonus.account'),
			placeholder: translated('add-bonus.account'),
			errorProps: { 'data-test-id': 'add-bonus-account-select-error' },
			error: combine(
				[model.form.fields.accountId.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-bonus.account'),
					}),
			),
			searchable: true,
			required: true,
		},
	});

	const CurrencyField = reflect({
		view: Select,
		bind: {
			'data-test-id': 'add-bonus-currency-select',
			value: model.form.fields.currency.$value,
			onChange: model.form.fields.currency.change,
			data: model.$currencies,
			disabled: model.form.fields.currency.$disabled,
			label: translated('add-bonus.currency'),
			placeholder: translated('add-bonus.currency'),
			errorProps: { 'data-test-id': 'add-bonus-currency-select-error' },
			error: combine(
				[model.form.fields.currency.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-bonus.currency'),
					}),
			),
			searchable: true,
			required: true,
		},
	});

	const GameField = reflect({
		view: Select,
		bind: {
			'data-test-id': 'add-bonus-game-select',
			value: model.form.fields.gameId.$value.map((value) => value?.toString() || null),
			onChange: (value: string | null) =>
				model.form.fields.gameId.change(value === null ? value : Number(value)),
			data: model.$games,
			disabled: model.form.fields.gameId.$disabled,
			label: translated('add-bonus.game'),
			placeholder: translated('add-bonus.game'),
			errorProps: { 'data-test-id': 'add-bonus-game-select-error' },
			error: combine(
				[model.form.fields.gameId.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-bonus.account'),
					}),
			),
			searchable: true,
			required: true,
		},
	});

	const DepositRateField = reflect({
		view: NumberInput,
		bind: {
			required: true,
			decimalScale: 2,
			rightSection: '%',
			'data-test-id': 'add-bonus-deposit-rate-input',
			label: translated('add-bonus.deposit-rate'),
			placeholder: translated('add-bonus.deposit-rate-placeholder'),
			value: model.form.fields.depositRate.$value,
			onChange: (value) => model.form.fields.depositRate.change(String(value ?? '')),
			error: combine(
				[model.form.fields.depositRate.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-bonus.deposit-rate'),
					}),
			),
		},
	});

	const ActivationThresholdField = reflect({
		view: NumberInput,
		bind: {
			required: true,
			decimalScale: 2,
			rightSection: model.form.fields.currency.$value,
			'data-test-id': 'add-bonus-activation-threshold-input',
			label: translated('add-bonus.activation-threshold'),
			placeholder: translated('add-bonus.activation-threshold-placeholder'),
			value: model.form.fields.activationThreshold.$value,
			onChange: (value) => model.form.fields.activationThreshold.change(String(value ?? '')),
			error: combine(
				[model.form.fields.activationThreshold.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-bonus.activation-threshold'),
					}),
			),
		},
	});

	const AddBonusModalButton = reflect({
		view: ModalForm.Button,
		bind: {
			'data-test-id': 'add-bonus-button',
			leftSection: <Plus />,
			className: cls.addJackpotModalButton,
			disabled: viewerModel.$role.map((role) => role !== USER_ROLES.god),
			children: translated('add-bonus.button-label'),
			modalData: null,
		},
	});

	const AddBonusModal = () => {
		const { t } = useTranslation();

		const pending = useUnit(model.$pending);

		return (
			<ModalForm
				title={t('add-bonus.title')}
				cancelButtonProps={{
					children: t('add-bonus.cancel'),
				}}
				submitButtonProps={{
					children: t('add-bonus.add'),
					loading: pending,
				}}
			>
				<AccountField />
				<GameField />
				<CurrencyField />
				<DepositRateField />
				<ActivationThresholdField />
			</ModalForm>
		);
	};

	return Object.assign(AddBonusModal, {
		Button: AddBonusModalButton,
	});
};
